import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import './NavigationLayout.scss';
import { Layout } from 'antd';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import UserProfile from 'components/UserProfile/UserProfile';
import type { MenuProps } from 'antd';
import { Menu } from 'antd';
import { useIntl } from 'react-intl';
import JobsMenuIcon from 'assets/icons/job-menu-icon.svg';
import ChidoriConnectMenuIcon from 'assets/icons/connect-icon.svg';
import SparkStarIcon from 'assets/icons/spark-star.svg';
import ChidoriIconName from 'assets/icons/chidori-logo-new-one-line.svg';
import LanguageChanger from 'components/LanguageChanger/LanguageChanger';
import { AppContext } from 'contexts/AppContext';
import useSignedURL from 'hooks/useSignedURL';
import { createSparkHistoryServerLink } from 'utils/cluster';
import { useGetClusterInfo } from 'hooks/cluster';
import { BarChartOutlined, HomeOutlined } from '@ant-design/icons';
import useChidoriRoutes from 'hooks/useChidoriRoutes';
import { menuItemKey } from 'types/types';
import Settings from 'components/Settings/Settings';
import clsx from 'clsx';

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  disabled: boolean = false,
  children?: MenuItem[],
  type?: 'group'
): MenuItem {
  return { key, icon, children, label, type, disabled } as MenuItem;
}

const { Content } = Layout;

const NavigationLayout: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { rootURL } = useContext(AppContext);
  const { isSignedURL } = useSignedURL();
  const { menuUrlMapping, defaultMenuKey } = useChidoriRoutes();
  const { clusterName } = useContext(AppContext);
  const { data: clusterInfo } = useGetClusterInfo({ clusterName });
  const sparkHistoryServerLink = clusterInfo?.Url
    ? createSparkHistoryServerLink(clusterInfo?.Url)
    : '';

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const lastScrolledRef = useRef(false);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;

    const SCROLL_THRESHOLD = 0;

    const onScroll = () => {
      const isScrolled = el.scrollTop > SCROLL_THRESHOLD;

      // Only update state when crossing the threshold, not every scroll event
      if (isScrolled !== lastScrolledRef.current) {
        setScrolled(isScrolled);
        lastScrolledRef.current = isScrolled;
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as Element;
    const isInsideAntPopup = !!target.closest?.(
      '.ant-select-dropdown, .ant-dropdown, .ant-popover, .ant-tooltip, .ant-modal-root'
    );
    if (isInsideAntPopup) return;
    if (dropdownRef.current && !dropdownRef.current.contains(target as Node)) {
      setDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const navItems: MenuProps['items'] = [
    ...(isSignedURL
      ? []
      : [
          getItem(
            <label className="navigation-layout__menu-item-wrapper">
              {intl.formatMessage({ id: 'sidebarMenu.overview' })}
            </label>,
            menuItemKey.OVERVIEW,
            <HomeOutlined  style={{ color: 'blue' }} />
          ),
        ]),
    getItem(
      <label className="navigation-layout__menu-item-wrapper">
        {intl.formatMessage({ id: 'sidebarMenu.jobs' })}
      </label>,
      menuItemKey.JOBS,
      <img src={JobsMenuIcon} alt="jobs" />
    ),
    ...(isSignedURL
      ? []
      : [
          getItem(
            <label className="navigation-layout__menu-item-wrapper">
              {intl.formatMessage({ id: 'chidoriConnect.connect' })}
            </label>,
            menuItemKey.CHIDORI_CONNECT,
            <img
              src={ChidoriConnectMenuIcon}
              alt="Connect"
              className="navigation-layout__connect-icon"
            />
          ),
          getItem(
            <label className="navigation-layout__menu-item-wrapper">
              {intl.formatMessage({ id: 'sidebarMenu.metrics' })}
            </label>,
            menuItemKey.METRICS,
            <BarChartOutlined style={{ color: 'blue' }} />
          ),
        ]),
  ];

  const onMenuItemClick: MenuProps['onClick'] = (e) => {
    if (!isSignedURL) {
      const navigationUrl = menuUrlMapping.get(e.key as menuItemKey);
      if (navigationUrl) navigate(navigationUrl);
    }
  };

  return (
    <Layout className="navigation-layout__wrapper">
      <header
        className={clsx('navigation-layout__header', {
          'navigation-layout__header--scrolled': scrolled,
        })}>
        <Link to={rootURL} className="navigation-layout__header-logo">
          <img src={ChidoriIconName} alt="Chidori" />
        </Link>

        <span className="navigation-layout__header-sep" />

        <nav className="navigation-layout__header-nav">
          <Menu
            onClick={onMenuItemClick}
            mode="horizontal"
            items={navItems}
            selectedKeys={[defaultMenuKey]}
            className="navigation-layout__header-menu"
          />
        </nav>

        <div className="navigation-layout__menu-wrapper" ref={dropdownRef}>
          {!isSignedURL && (
            <Link
              to={sparkHistoryServerLink}
              target="_blank"
              rel="noopener noreferrer"
              className="navigation-layout__spark-pill">
              <img
                src={SparkStarIcon}
                alt=""
                aria-hidden="true"
                className="navigation-layout__spark-pill-icon"
              />
              <span>
                {intl.formatMessage({ id: 'sidebarMenu.sparkHistoryServer' })}
              </span>
            </Link>
          )}
          <button
            className={clsx('navigation-layout__menu-btn', {
              'navigation-layout__menu-btn--open': dropdownOpen,
            })}
            onClick={() => setDropdownOpen((prev) => !prev)}
            aria-label="Open account menu"
            aria-expanded={dropdownOpen}>
            <span className="navigation-layout__menu-line" />
            <span className="navigation-layout__menu-line" />
            <span className="navigation-layout__menu-line" />
          </button>

          {dropdownOpen && (
            <div className="navigation-layout__dropdown">
              <div
                className="navigation-layout__dropdown-row"
                onMouseDown={(e) => e.stopPropagation()}>
                <LanguageChanger />
              </div>

              {!isSignedURL && (
                <div
                  className="navigation-layout__dropdown-row"
                  onMouseDown={(e) => e.stopPropagation()}>
                  <Settings />
                </div>
              )}

              <div className="navigation-layout__dropdown-divider" />

              <div className="navigation-layout__dropdown-row navigation-layout__dropdown-row--profile">
                <UserProfile />
              </div>
            </div>
          )}
        </div>
      </header>

      <Layout
        className="navigation-layout__main"
        ref={mainRef as unknown as React.Ref<HTMLDivElement>}>
        <Content className="navigation-layout__content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default NavigationLayout;
